import type { AudioEngine } from './AudioEngine.js';

/**
 * Wood-impact SFX for the tree-felling system.
 *
 *   playHit(velocityMs, scale) — fired when the car crashes into a tree
 *   playLand(scale)            — fired when a felled tree finishes falling
 *
 * Both methods build a small one-shot node graph per trigger (oscillator(s)
 * + filtered noise burst + envelope), styled after `ImpactSound`. Sounds
 * route through `audio.master` so the master mute (M key) silences them.
 *
 * No per-layer mute — matches the existing `ImpactSound` pattern. If a
 * grouped "Impacts" toggle is wanted later, it would cover both classes.
 */
export class TreeImpactSound {
  private lastHitAt = 0;
  private lastLandAt = 0;
  /** Persistent gain nodes — every per-trigger outBus connects through these
   *  so volume / mute from the Settings → Sounds section can act on tree
   *  impacts the same as the looped layers. */
  private volumeGain: GainNode;
  private muteGain: GainNode;
  private volume = 1.0;
  private muted = false;

  constructor(private audio: AudioEngine, private noiseBuf: AudioBuffer) {
    this.volumeGain = audio.ctx.createGain();
    this.volumeGain.gain.value = this.volume;
    this.muteGain = audio.ctx.createGain();
    this.muteGain.gain.value = 1.0;
    this.volumeGain.connect(this.muteGain).connect(audio.master);
  }

  setVolume(v: number): void {
    this.volume = clamp01(v);
    this.volumeGain.gain.setTargetAtTime(this.volume, this.audio.ctx.currentTime, 0.05);
  }

  getVolume(): number {
    return this.volume;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.muteGain.gain.setTargetAtTime(m ? 0 : 1, this.audio.ctx.currentTime, 0.05);
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  /**
   * Car-hits-tree burst. Three layered components: high crack, low thud,
   * mid-band rustle. All gain-scaled by velocity (so brushing past a tree
   * is quieter than a 100 km/h impact); thud also pitched by tree size.
   *
   * Spatialisation: a shared per-trigger StereoPannerNode + attenuation
   * GainNode sit between the three component chains and master. Same
   * `pan`/`attenuation` model as `playLand` — symmetric pipeline.
   *
   * @param velocityMs   car speed in m/s
   * @param scale        tree size factor (default 1.0; bigger → lower thud)
   * @param pan          -1 = full left, +1 = full right, 0 = centre. Computed
   *                     by the subscriber from tree position vs. car forward.
   * @param attenuation  0..1 distance falloff multiplier. Caller is responsible
   *                     for the distance curve; 0 means "skip entirely".
   */
  playHit(velocityMs: number, scale = 1.0, pan = 0, attenuation = 1): void {
    const atn = clamp01(attenuation);
    if (atn <= 0) return; // out of audible range — skip the graph entirely
    const v = clamp01((velocityMs - 2) / 15);
    if (v <= 0) return;

    const ctx = this.audio.ctx;
    const now = ctx.currentTime;
    if (now - this.lastHitAt < 0.08) return; // debounce — matches ImpactSound
    this.lastHitAt = now;

    const dur = this.noiseBuf.duration;

    // --- Shared per-trigger output: attenuation gain → stereo pan → master ---
    // All three component chains feed `outBus`; one pan node spatialises the
    // whole impact rather than per-component, keeping the timbre coherent.
    const outBus = ctx.createGain();
    outBus.gain.value = atn;
    const outPan = ctx.createStereoPanner();
    outPan.pan.value = clampPan(pan);
    outBus.connect(outPan).connect(this.volumeGain);

    // --- Crack: high-frequency noise burst, snappy decay ---
    {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuf;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 2500;
      bp.Q.value = 1.5;
      const env = ctx.createGain();
      env.gain.setValueAtTime(0, now);
      env.gain.linearRampToValueAtTime(0.4 * v, now + 0.005);
      env.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
      src.connect(bp).connect(env).connect(outBus);
      src.start(now, Math.random() * Math.max(0, dur - 0.2), 0.2);
      src.stop(now + 0.2);
    }

    // --- Thud: sine wave dropping in pitch — the trunk shock ---
    {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(120 / scale, now);
      osc.frequency.exponentialRampToValueAtTime(50 / scale, now + 0.2);
      const env = ctx.createGain();
      env.gain.setValueAtTime(0, now);
      env.gain.linearRampToValueAtTime(0.5 * v * scale, now + 0.005);
      env.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
      osc.connect(env).connect(outBus);
      osc.start(now);
      osc.stop(now + 0.3);
    }

    // --- Rustle: mid-band noise, slower decay — branches & bark shaking ---
    {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuf;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 600;
      bp.Q.value = 0.7;
      const env = ctx.createGain();
      env.gain.setValueAtTime(0, now);
      env.gain.linearRampToValueAtTime(0.2 * v, now + 0.01);
      env.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
      src.connect(bp).connect(env).connect(outBus);
      src.start(now, Math.random() * Math.max(0, dur - 0.45), 0.45);
      src.stop(now + 0.45);
    }
  }

  /**
   * Tree-finishes-falling thud. Two components: heavy low thud + branch
   * crunch. Pitched by tree size (bigger = lower) with a slightly slower
   * attack than `playHit` so it reads as "weight settling" rather than
   * "sharp impact".
   *
   * Spatialised + distance-attenuated (see `playHit` for the same model).
   * This matters more for land than hit, because the car may have driven
   * 20+ m away in the seconds between impact and animation completion.
   *
   * @param scale        tree size factor (default 1.0)
   * @param pan          -1 (left) to +1 (right). 0 = centre.
   * @param attenuation  0..1 distance falloff. 0 → skip graph entirely.
   */
  playLand(scale = 1.0, pan = 0, attenuation = 1): void {
    const atn = clamp01(attenuation);
    if (atn <= 0) return;

    const ctx = this.audio.ctx;
    const now = ctx.currentTime;
    if (now - this.lastLandAt < 0.08) return;
    this.lastLandAt = now;

    const dur = this.noiseBuf.duration;

    // Shared per-trigger output bus (see playHit doc for rationale).
    const outBus = ctx.createGain();
    outBus.gain.value = atn;
    const outPan = ctx.createStereoPanner();
    outPan.pan.value = clampPan(pan);
    outBus.connect(outPan).connect(this.volumeGain);

    // --- Heavy thud: deeper than the hit-thud, slower attack ---
    {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(80 / scale, now);
      osc.frequency.exponentialRampToValueAtTime(35 / scale, now + 0.3);
      const env = ctx.createGain();
      env.gain.setValueAtTime(0, now);
      env.gain.linearRampToValueAtTime(0.6 * scale, now + 0.02);
      env.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
      osc.connect(env).connect(outBus);
      osc.start(now);
      osc.stop(now + 0.45);
    }

    // --- Branch crunch: filtered noise, slightly higher band than rustle ---
    {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuf;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 800;
      bp.Q.value = 0.6;
      const env = ctx.createGain();
      env.gain.setValueAtTime(0, now);
      env.gain.linearRampToValueAtTime(0.3 * scale, now + 0.01);
      env.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
      src.connect(bp).connect(env).connect(outBus);
      src.start(now, Math.random() * Math.max(0, dur - 0.4), 0.4);
      src.stop(now + 0.4);
    }
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clampPan(v: number): number {
  return v < -1 ? -1 : v > 1 ? 1 : v;
}
